import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Building2, ChevronRight, Search, User, UserPlus, Users, X } from 'lucide-react'
import { useDatos } from '../store/datos'
import { Encabezado, Pagina } from '../components/Layout'
import { SubNavVentas } from '../components/SubNavVentas'
import { Avatar, Boton, Chip, Tarjeta, Vacio } from '../components/ui/base'
import { FormularioCliente } from '../components/FormularioCliente'
import { cn } from '../lib/cn'
import { money, normalizar, relativo } from '../lib/format'
import { rankingClientes } from '../lib/metricas'
import { RUBROS, type TipoCliente } from '../types'

function tipoDeParam(v: string | null): TipoCliente | null {
  return v === 'empresa' || v === 'particular' ? v : null
}

export function Clientes() {
  const { clientes, ventas } = useDatos()
  const navegar = useNavigate()
  const [params] = useSearchParams()
  // Cuando se llega desde "Nueva venta" (botón Empresa/Particular), esta
  // misma pantalla hace de selector: se salta la portada, va directo a
  // la lista de ese grupo, y elegir un cliente vuelve a la venta en vez
  // de abrir su ficha.
  const paraVenta = tipoDeParam(params.get('paraVenta'))
  const [vista, setVista] = useState<TipoCliente | null>(paraVenta)
  const [texto, setTexto] = useState('')
  const [nuevo, setNuevo] = useState(false)

  useEffect(() => {
    if (paraVenta) setVista(paraVenta)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paraVenta])

  const stats = useMemo(() => {
    const mapa = new Map(rankingClientes(ventas).map((r) => [r.clienteId, r]))
    return mapa
  }, [ventas])

  const empresas = clientes.filter((c) => c.tipo === 'empresa')
  const particulares = clientes.filter((c) => c.tipo === 'particular')

  const lista = useMemo(() => {
    if (!vista) return []
    const q = normalizar(texto.trim())
    return clientes
      .filter((c) => c.tipo === vista)
      .filter(
        (c) =>
          !q ||
          [c.nombre, c.apellido, c.rut, c.comuna, c.contacto, c.email, c.telefono].some(
            (campo) => campo && normalizar(campo).includes(q),
          ),
      )
      .sort((a, b) => {
        const va = stats.get(a.id)?.total ?? 0
        const vb = stats.get(b.id)?.total ?? 0
        if (va !== vb) return vb - va
        return a.nombre.localeCompare(b.nombre, 'es')
      })
  }, [clientes, vista, texto, stats])

  /* -------- Portada: los dos grupos, como pidió el flujo -------- */
  if (!vista) {
    return (
      <Pagina>
        <SubNavVentas activa="clientes" />
        <Encabezado
          titulo="Clientes"
          detalle={`${clientes.length} en total · ${money(ventas.reduce((s, v) => s + v.total, 0))} vendidos históricamente`}
          accion={
            <Boton icono={<UserPlus className="size-[18px]" />} onClick={() => setNuevo(true)}>
              Nuevo cliente
            </Boton>
          }
        />

        <div className="grid gap-4 escalonar sm:grid-cols-2">
          <PuertaGrupo
            titulo="Empresas"
            detalle="Pastelerías, restaurantes, cafeterías, hoteles y distribuidores"
            cantidad={empresas.length}
            total={empresas.reduce((s, c) => s + (stats.get(c.id)?.total ?? 0), 0)}
            icono={<Building2 className="size-7" />}
            tono="from-tinta-500 to-tinta-700"
            onClick={() => setVista('empresa')}
          />
          <PuertaGrupo
            titulo="Clientes particulares"
            detalle="Ventas directas a personas"
            cantidad={particulares.length}
            total={particulares.reduce((s, c) => s + (stats.get(c.id)?.total ?? 0), 0)}
            icono={<User className="size-7" />}
            tono="from-magenta-500 to-magenta-700"
            onClick={() => setVista('particular')}
          />
        </div>

        {/* Los que más compran, como atajo a su ficha. */}
        <h3 className="mt-8 mb-3 text-base font-bold texto-base">Los que más compran</h3>
        <Tarjeta className="overflow-hidden">
          {ventas.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm texto-tenue">
              Cuando registres ventas aparecerá aquí el ranking.
            </p>
          ) : (
            <ul className="divide-y borde-suave">
              {rankingClientes(ventas)
                .slice(0, 6)
                .map((r, i) => (
                  <li key={r.clienteId}>
                    <Link
                      to={`/clientes/${r.clienteId}`}
                      className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:superficie-hundida"
                    >
                      <span className="cifra w-5 shrink-0 text-[13px] font-bold texto-tenue">
                        {i + 1}
                      </span>
                      <Avatar nombre={r.nombre} tamano="sm" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-semibold texto-base">
                          {r.nombre}
                        </span>
                        <span className="block truncate text-[12px] texto-suave">
                          {r.compras} {r.compras === 1 ? 'compra' : 'compras'} · última{' '}
                          {relativo(r.ultima)}
                        </span>
                      </span>
                      <span className="cifra shrink-0 text-[15px] font-bold texto-base">
                        {money(r.total)}
                      </span>
                    </Link>
                  </li>
                ))}
            </ul>
          )}
        </Tarjeta>

        <FormularioCliente abierto={nuevo} onCerrar={() => setNuevo(false)} />
      </Pagina>
    )
  }

  /* -------- Listado del grupo elegido -------- */
  const esEmpresa = vista === 'empresa'

  return (
    <Pagina>
      {!paraVenta && <SubNavVentas activa="clientes" />}

      {paraVenta ? (
        <button
          onClick={() => navegar(`/vender?tipo=${paraVenta}`)}
          className="mb-4 inline-flex items-center gap-1.5 text-[14px] font-semibold texto-suave transition-colors hover:texto-base"
        >
          <ArrowLeft className="size-4" /> Volver a la venta
        </button>
      ) : (
        <button
          onClick={() => {
            setVista(null)
            setTexto('')
          }}
          className="mb-4 inline-flex items-center gap-1.5 text-[14px] font-semibold texto-suave transition-colors hover:texto-base"
        >
          <ChevronRight className="size-4 rotate-180" /> Clientes
        </button>
      )}

      {paraVenta && (
        <p className="mb-4 rounded-2xl bg-bosque-50 px-4 py-3 text-[13px] font-medium text-bosque-700 dark:bg-bosque-500/10 dark:text-bosque-200">
          Elige {esEmpresa ? 'la empresa' : 'el particular'} al que le vendes. Se aplica el precio{' '}
          {esEmpresa ? 'mayorista' : 'al detalle'} que le corresponde.
        </p>
      )}

      <Encabezado
        titulo={esEmpresa ? 'Empresas' : 'Clientes particulares'}
        detalle={`${lista.length} ${lista.length === 1 ? 'registro' : 'registros'}`}
        accion={
          <Boton icono={<UserPlus className="size-[18px]" />} onClick={() => setNuevo(true)}>
            Nuevo
          </Boton>
        }
      />

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 texto-tenue" />
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={esEmpresa ? 'Nombre, RUT o comuna…' : 'Nombre, teléfono o comuna…'}
          className="h-12 w-full rounded-2xl border borde-base superficie-elevada pr-11 pl-11 text-[15px] texto-base transition-colors placeholder:texto-tenue focus:border-bosque-400 focus:outline-none focus:ring-4 focus:ring-bosque-500/10"
        />
        {texto && (
          <button
            onClick={() => setTexto('')}
            className="absolute top-1/2 right-3 grid size-8 -translate-y-1/2 place-items-center rounded-lg texto-tenue hover:texto-base"
            aria-label="Borrar búsqueda"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {lista.length === 0 ? (
        <Vacio
          icono={<Users className="size-7" />}
          titulo={texto ? 'Nadie coincide con esa búsqueda' : 'Todavía no hay registros'}
          detalle={
            texto
              ? 'Prueba con otro nombre, RUT o comuna.'
              : esEmpresa
                ? 'Agrega la primera empresa para no volver a escribir sus datos.'
                : 'Agrega el primer cliente particular.'
          }
          accion={
            !texto ? (
              <Boton onClick={() => setNuevo(true)} icono={<UserPlus className="size-[18px]" />}>
                Nuevo cliente
              </Boton>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-3 escalonar sm:grid-cols-2 xl:grid-cols-3">
          {lista.map((c) => {
            const s = stats.get(c.id)
            return (
              <Link
                key={c.id}
                to={paraVenta ? `/vender?cliente=${c.id}` : `/clientes/${c.id}`}
                className="block"
              >
                <Tarjeta className="group h-full cursor-pointer p-4 transition-all duration-200 ease-[var(--ease-suave)] hover:-translate-y-0.5 hover:sombra-flotante">
                  <div className="flex items-start gap-3">
                    <Avatar nombre={c.nombre} />
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-[15px] font-bold texto-base">
                        {[c.nombre, c.apellido].filter(Boolean).join(' ')}
                      </h3>
                      <p className="mt-0.5 truncate text-[12px] texto-suave">
                        {[c.comuna, c.telefono].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                      </p>
                      {esEmpresa && c.rubro && (
                        <Chip className="mt-2">
                          {RUBROS.find((r) => r.valor === c.rubro)?.etiqueta}
                        </Chip>
                      )}
                    </div>
                    <ChevronRight className="mt-1 size-4 shrink-0 texto-tenue transition-transform group-hover:translate-x-0.5" />
                  </div>

                  <div className="mt-3.5 flex items-end justify-between border-t borde-suave pt-3">
                    <div>
                      <p className="text-[11px] font-semibold texto-tenue uppercase">Compras</p>
                      <p className="cifra text-[15px] font-bold texto-base">{s?.compras ?? 0}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] font-semibold texto-tenue uppercase">
                        Total histórico
                      </p>
                      <p className="cifra text-[15px] font-bold texto-base">{money(s?.total ?? 0)}</p>
                    </div>
                  </div>
                </Tarjeta>
              </Link>
            )
          })}
        </div>
      )}

      <FormularioCliente
        abierto={nuevo}
        onCerrar={() => setNuevo(false)}
        tipoInicial={vista}
        onGuardado={paraVenta ? (id) => navegar(`/vender?cliente=${id}`) : undefined}
      />
    </Pagina>
  )
}

function PuertaGrupo({
  titulo,
  detalle,
  cantidad,
  total,
  icono,
  tono,
  onClick,
}: {
  titulo: string
  detalle: string
  cantidad: number
  total: number
  icono: React.ReactNode
  tono: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative overflow-hidden rounded-3xl bg-gradient-to-br p-6 text-left text-white',
        'transition-all duration-300 ease-[var(--ease-suave)] hover:-translate-y-1 hover:shadow-xl active:translate-y-0',
        tono,
      )}
    >
      <div
        className="absolute -top-16 -right-12 size-44 rounded-full bg-white/10 transition-transform duration-500 group-hover:scale-125"
        aria-hidden
      />
      <div className="relative">
        <div className="mb-5 grid size-14 place-items-center rounded-2xl bg-white/15 backdrop-blur-sm">
          {icono}
        </div>
        <h3 className="font-display text-2xl font-bold">{titulo}</h3>
        <p className="mt-1 max-w-[26ch] text-[13px] text-white/75">{detalle}</p>
        <div className="mt-5 flex items-end gap-6">
          <div>
            <p className="cifra font-display text-3xl font-bold">{cantidad}</p>
            <p className="text-[11px] font-semibold text-white/70 uppercase">registros</p>
          </div>
          <div>
            <p className="cifra font-display text-lg font-bold">{money(total)}</p>
            <p className="text-[11px] font-semibold text-white/70 uppercase">histórico</p>
          </div>
          <ChevronRight className="mb-1 ml-auto size-6 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </button>
  )
}
